import { emptyCondition, mergeConditionsInTree } from './criteriaUtils';
import { audienceRuleCount, isAudience, normalizeAudience } from '../../services/offerStudioService';

export function stampRuleNode(
  node,
  { offer_feature, offer_value, offer_value_label, label, direction }
) {
  if (!node || typeof node !== 'object') return node;
  const out = { ...node };
  out.offer_feature = offer_feature;
  out.offer_value = offer_value;
  if (offer_value_label != null) out.offer_value_label = offer_value_label;
  out.offer_direction = direction;
  if (out.kind === 'group') {
    if (label) out.display_name = label;
    out.children = (out.children || []).map((child) =>
      stampRuleNode(child, {
        offer_feature,
        offer_value,
        offer_value_label,
        label: null,
        direction,
      })
    );
  } else if (label && !out.display_name) {
    out.display_name = label;
  }
  return out;
}

export function parseStoredRule(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** Include/exclude criteria node from a value-rules payload. Exclude is stored, not negated. */
export function nodeFromValueRule(row, { exclude = false } = {}) {
  const feature = String(row?.feature || '').trim();
  const value = String(row?.top_conv_value ?? row?.value ?? '').trim();
  const raw = parseStoredRule(exclude ? row?.exclude_rule : row?.include_rule);
  if (!feature || !value || !raw) return null;
  const label = exclude ? row?.exclude_label : row?.include_label;
  return stampRuleNode(raw, {
    offer_feature: feature,
    offer_value: value,
    offer_value_label: row?.top_conv_value_label || row?.value_label || row?.offer_value_label,
    label,
    direction: exclude ? 'under' : 'over',
  });
}

function normalizeSeedNode(rule) {
  // Stored group rules arrive from cube_offer_metrics_value_rules as loader-written JSON,
  // so their operator is checked here rather than trusted all the way to the backend.
  if (rule?.kind === 'group') {
    return { ...rule, op: groupOp(rule.op), children: (rule.children || []).map(normalizeSeedNode) };
  }
  return { ...emptyCondition(), ...rule };
}

/**
 * A criteria group combines with AND/OR, while the offer report's match toggle reads
 * ALL/ANY. Accept either so a caller handing over its own wording cannot build a tree
 * the backend rejects.
 */
function groupOp(matchOp) {
  const v = String(matchOp || '').trim().toUpperCase();
  return v === 'OR' || v === 'ANY' ? 'OR' : 'AND';
}

/**
 * Build editor criteria from Audience Studio rules.
 * ALL/ANY applies only to includes; every exclude is ANDed in.
 *
 * Picks that land on the same column are restated as one `in` / `not_in`, because
 * two values of one attribute mean "either" — ANDing them would ask for a profile
 * whose gender is both M and F.
 */
function criteriaFromFlatRules(list, matchOp) {
  const op = groupOp(matchOp);
  const includes = [];
  const excludes = [];
  list.forEach((r) => {
    const node = normalizeSeedNode(r);
    if (r?.offer_direction === 'under') excludes.push(node);
    else includes.push(node);
  });
  if (!excludes.length) {
    return mergeConditionsInTree({ kind: 'group', op, children: includes });
  }
  if (!includes.length) {
    return mergeConditionsInTree({ kind: 'group', op: 'AND', children: excludes });
  }
  return mergeConditionsInTree({
    kind: 'group',
    op: 'AND',
    children: [
      { kind: 'group', op, children: includes },
      ...excludes,
    ],
  });
}

function criteriaFromAudience(audience, matchOp) {
  const a = normalizeAudience(audience);
  const filled = a.groups
    .map((g, i) => ({ g, i }))
    .filter(({ g }) => (g.rules || []).length);
  // A single unnamed cohort still honours ALL/ANY. Once a second group slot
  // exists, each filled cohort stays its own AND — flattening them made a
  // switch back to group 1 look like one global condition list.
  if (a.groups.length <= 1) {
    return criteriaFromFlatRules([...(a.groups[0]?.rules || []), ...a.globalExcludes], matchOp);
  }
  const cohorts = filled.map(({ g, i }) => {
    const tree = criteriaFromFlatRules(g.rules, 'AND');
    return { ...tree, display_name: g.name || `Group ${i + 1}` };
  });
  if (!cohorts.length) {
    return criteriaFromFlatRules(a.globalExcludes, 'AND');
  }
  const union = cohorts.length === 1 ? cohorts[0] : { kind: 'group', op: groupOp(a.groupsOp), children: cohorts };
  if (!a.globalExcludes.length) return union;
  return mergeConditionsInTree({
    kind: 'group',
    op: 'AND',
    children: [union, ...a.globalExcludes.map(normalizeSeedNode)],
  });
}

/**
 * Build editor criteria from Audience Studio rules.
 * ALL/ANY applies only to includes in a single unnamed cohort; every exclude is
 * ANDed in. Two or more cohorts become a union (OR by default), each ANDed
 * inside, with excludes still outside everyone.
 *
 * Picks that land on the same column are restated as one `in` / `not_in`, because
 * two values of one attribute mean "either" — ANDing them would ask for a profile
 * whose gender is both M and F.
 */
export function criteriaFromOfferSeed(rules, matchOp) {
  if (isAudience(rules)) {
    if (!audienceRuleCount(rules)) return { kind: 'group', op: 'AND', children: [] };
    return criteriaFromAudience(rules, matchOp);
  }
  const list = Array.isArray(rules) ? rules : [];
  return criteriaFromFlatRules(list, matchOp);
}
